import React from 'react';
import { Box, Typography } from '@mui/material';
import { gray80 } from '../../constants/colors';
import { MatchFilterValue } from '../../types/masterReconciliation';

/**
 * 照合理由ラベルの表示（#3309）。
 *
 * Firestore に保存されているラベルは1本の合成文字列で、絞り込み条件まで
 * 括弧内に畳み込まれているため横に長く読みにくい。
 *
 *   マスタと完全一致（絞り込み：得意先ID / 絞り込み未適用：納入先ID）（照合結果：ベッドフレーム）
 *
 * これを次のように行で分けて出す。
 *
 *   マスタと完全一致（照合結果：**ベッドフレーム**）
 *   得意先ID：C001
 *   納入先ID：未適用
 *
 * **保存されている文字列自体は変えない。** 要注意判定（reviewIssues）は
 * match_reason が無い既存データに対してこのラベルの部分文字列で判定する
 * フォールバックを持っており、形を変えるとその判定が壊れるため、
 * ここでは表示の直前に読み替えるだけにする。
 *
 * 絞り込みの「値」は保存ラベルに含まれない（列名のみ）。照合時の値は
 * result_data の match_filter_values に別で保存し、呼び出し側が filterValues で渡す（#3517）。
 * 以前は項目の現在値を出していたが、照合後に値を書き換えると
 * 「その値で照合した」ように見えるためやめた。
 * match_filter_values を持たない既存データは照合時の値が分からないため、
 * 値は出さずにラベル内の列名（絞り込み：得意先CD など）だけを出す。
 */

interface MatchReasonLabelProps {
  /** Firestore / 選択状態から取得した合成ラベル */
  label: string;
  /**
   * 照合時の絞り込みの値（result_data の match_filter_values）。
   * 未保存（既存データ）なら undefined を渡す。その場合はラベル内の列名だけを出す。
   */
  filterValues?: MatchFilterValue[];
  /** 明細セルと単一項目で余白が違うため呼び出し側で指定する */
  marginBottom?: string;
}

/** 末尾の「（照合結果：〜）」「（変換結果：〜）」を切り出す */
const RESULT_SUFFIX = /（(照合結果|変換結果)：(.*)）\s*$/;
/** 畳み込まれている絞り込みブロック。行に分けて出すのでラベルからは外す */
const FILTER_BLOCK = /（絞り込み(?:未適用)?：[^（）]*(?: \/ 絞り込み(?:未適用)?：[^（）]*)*）/g;

/** 絞り込みブロックの中身（「絞り込み：A, B」「絞り込み未適用：C」）を1行ずつ取り出す */
export const parseFilterBlockLines = (label: string): string[] =>
  Array.from(label.matchAll(FILTER_BLOCK)).flatMap(block =>
    block[0]
      .slice(1, -1)
      .split(' / ')
      .map(line => line.trim())
      .filter(line => line !== ''),
  );

/** 照合時の絞り込みの値を1行の表示にする（#3517） */
export const formatFilterValue = (filterValue: MatchFilterValue): string => {
  if (filterValue.filter_value !== null) {
    return `${filterValue.item_label}：${filterValue.filter_value}`;
  }
  return `${filterValue.item_label}：${filterValue.excluded ? '外して照合' : '未適用'}`;
};

/** 合成ラベルを「理由」と「照合結果の値」に分解する */
export const parseMatchReasonLabel = (
  label: string,
): { reason: string; resultLabel?: string; resultValue?: string } => {
  const matched = RESULT_SUFFIX.exec(label);
  const withoutResult = matched ? label.slice(0, matched.index) : label;
  // 絞り込みは行に分けて出すため、理由の行からは取り除く
  const reason = withoutResult.replace(FILTER_BLOCK, '').trim();

  if (!matched) return { reason };
  return { reason, resultLabel: matched[1], resultValue: matched[2] };
};

/**
 * 照合理由を複数行で表示する。
 *
 * 値（照合結果・絞り込みの値）は読み手が確認したい情報なので、
 * 照合結果の値だけを太字にして拾いやすくする。
 */
export const MatchReasonLabel: React.FC<MatchReasonLabelProps> = ({
  label,
  filterValues,
  marginBottom,
}) => {
  const { reason, resultLabel, resultValue } = parseMatchReasonLabel(label);
  // 照合時の値があればそれを、無い既存データはラベル内の列名だけを出す（#3517）
  const filterLines = filterValues
    ? filterValues.map(formatFilterValue)
    : parseFilterBlockLines(label);

  const textSx = {
    fontSize: '11px',
    color: gray80,
    lineHeight: 1.3,
    textAlign: 'left' as const,
    userSelect: 'text' as const,
    cursor: 'text' as const,
    wordBreak: 'break-word' as const,
  };

  return (
    <Box sx={{ mt: '2px', ...(marginBottom ? { mb: marginBottom } : {}) }}>
      <Typography sx={textSx}>
        {reason}
        {resultValue !== undefined && (
          <>
            （{resultLabel}：
            <Box component="span" sx={{ fontWeight: 700 }}>
              {resultValue}
            </Box>
            ）
          </>
        )}
      </Typography>
      {filterLines.map(line => (
        <Typography key={line} sx={textSx}>
          {line}
        </Typography>
      ))}
    </Box>
  );
};

export default MatchReasonLabel;
